import { CommonModule } from '@angular/common';
import { Component, effect, Inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatBottomSheet, MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { Router } from '@angular/router';
import { LucideLogOut } from '@lucide/angular';
import { catchError, forkJoin, of, switchMap } from 'rxjs';
import { AuthService } from '../../../core/auth/auth.service';
import { CompetitionHttpService } from '../../competition/registration/services/competition-http.service';
import { ProfileHttpService } from '../../competition/registration/services/profile-http.service';
import { TermsSheetComponent } from '../terms-sheet/terms-sheet.component';
import { APP_CONFIG, AppConfig } from '../../../app.config';

@Component({
  selector: 'app-registration',
  standalone: true,
  imports: [TermsSheetComponent, MatBottomSheetModule, LucideLogOut, CommonModule, FormsModule, ReactiveFormsModule, TermsSheetComponent, LucideLogOut],
  templateUrl: './registration.component.html',
  styleUrl: './registration.component.scss'
})
export class RegistrationComponent {
  registrationForm: FormGroup;

  isAccepted = signal(false);

  positions = [
    'Dianteiro esquerdo',
    'Atacante',
    'Dianteiro direito',
    'Extremo Esquerdo',
    'Centro-avante',
    'Extremo direito',
    'Médio ofensivo esquerdo',
    'Meio-campista central ofensivo',
    'Médio ofensivo direito',
    'Meio-campista esquerdo',
    'Meio-campista central esquerdo',
    'Meio-campista central',
    'Meio-campista centro direito',
    'Meio-campista direito',
    'Meio-campista esquerdo defensivo',
    'Meio-campista central defensivo',
    'Meio-campista direito defensivo',
    'Zagueiro Extremo Esquerdo',
    'Zagueiro Extremo direito',
    'Zagueiro Lateral esquerdo',
    'Zagueiro central esquerdo',
    'Zagueiro',
    'Zagueiro central direito',
    'Zagueiro Lateral direito',
    'Especial',
    'Goleiro',
    'Nenhum'
  ];

  countries = [
    { code: '+55', flag: '🇧🇷', name: 'Brazil' },
    { code: '+49', flag: '🇩🇪', name: 'Germany' },
    { code: '+33', flag: '🇫🇷', name: 'France' },
    { code: '+421', flag: '🇸🇰', name: 'SK' },
    { code: '+44', flag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', name: 'GB' },
    { code: '+351', flag: '🇵🇹', name: 'Portugal' },
    { code: '+34', flag: '🇪🇸', name: 'Spain' },
    { code: 'diferente', flag: '', name: 'Other' }
  ];

  competitionId = '';
  hasClub = false;

  constructor(private fb: FormBuilder, public authService: AuthService, private profileHttp: ProfileHttpService, private competitionHttp: CompetitionHttpService, private router: Router, private bottomSheet: MatBottomSheet, @Inject(APP_CONFIG) private readonly config: AppConfig) {
    effect(() => {
      this.competitionId = config.competitionId;

      const profile = this.authService.userProfile();

      if (profile && profile.details) {
        this.registrationForm.patchValue({
          firstName: profile.details.firstName,
          lastName: profile.details.lastName,
          city: profile.details.placeOfResidence,
          position: this.mapAbbreviationToPosition(profile.firstPosition),
          club: profile.details.activeClub,
        })
      }

      if (this.registrationForm.get('club')?.value) {
        this.registrationForm.get('club')?.disable();
        this.hasClub = true;
      }
    })

    this.registrationForm = this.fb.group({
      firstName: ['', { validators: [Validators.required] }],
      lastName: ['', { validators: [Validators.required] }],
      countryCode: ['+55', { validators: [Validators.required] }],
      phoneNumber: ['', { validators: [Validators.required] }],
      city: ['', { validators: [Validators.required] }],
      position: ['', { validators: [Validators.required] }],
      club: [''],
      agreedToTerms: [false, { validators: [Validators.requiredTrue] }]
    });
  }

  mapPositionToAbbreviation(position: string) {
    switch (position) {
      case 'Dianteiro esquerdo':
        return 'ls';

      case 'Atacante':
        return 'st';

      case 'Dianteiro direito':
        return 'rs';

      case 'Extremo Esquerdo':
        return 'lw';

      case 'Centro-avante':
        return 'cf';

      case 'Extremo direito':
        return 'rw';

      case 'Médio ofensivo esquerdo':
        return 'lam';

      case 'Meio-campista central ofensivo':
        return 'cam';

      case 'Médio ofensivo direito':
        return 'ram';

      case 'Meio-campista esquerdo':
        return 'lm';

      case 'Meio-campista central esquerdo':
        return 'lcm';

      case 'Meio-campista central':
        return 'cm';

      case 'Meio-campista centro direito':
        return 'rcm';

      case 'Meio-campista direito':
        return 'rm';

      case 'Meio-campista esquerdo defensivo':
        return 'ldm';

      case 'Meio-campista central defensivo':
        return 'cdm';

      case 'Meio-campista direito defensivo':
        return 'rdm';

      case 'Zagueiro Extremo Esquerdo':
        return 'lwb';

      case 'Zagueiro Extremo direito':
        return 'rwb';

      case 'Zagueiro Lateral esquerdo':
        return 'lb';

      case 'Zagueiro central esquerdo':
        return 'lcb';

      case 'Zagueiro':
        return 'cb';

      case 'Zagueiro central direito':
        return 'rcb';

      case 'Zagueiro Lateral direito':
        return 'rb';

      case 'Especial':
        return 'sw';

      case 'Goleiro':
        return 'gk';

      case 'Nenhum':
        return 'none';

      default:
        return '';
    }
  }

  mapAbbreviationToPosition(abbreviation: string) {
    switch (abbreviation) {
      case 'ls':
        return 'Dianteiro esquerdo';

      case 'st':
        return 'Atacante';

      case 'rs':
        return 'Dianteiro direito';

      case 'lw':
        return 'Extremo Esquerdo';

      case 'cf':
        return 'Centro-avante';

      case 'rw':
        return 'Extremo direito';

      case 'lam':
        return 'Médio ofensivo esquerdo';

      case 'cam':
        return 'Meio-campista central ofensivo';

      case 'ram':
        return 'Médio ofensivo direito';

      case 'lm':
        return 'Meio-campista esquerdo';

      case 'lcm':
        return 'Meio-campista central esquerdo';

      case 'cm':
        return 'Meio-campista central';

      case 'rcm':
        return 'Meio-campista centro direito';

      case 'rm':
        return 'Meio-campista direito';

      case 'ldm':
        return 'Meio-campista esquerdo defensivo';

      case 'cdm':
        return 'Meio-campista central defensivo';

      case 'rdm':
        return 'Meio-campista direito defensivo';

      case 'lwb':
        return 'Zagueiro Extremo Esquerdo';

      case 'rwb':
        return 'Zagueiro Extremo direito';

      case 'lb':
        return 'Zagueiro Lateral esquerdo';

      case 'lcb':
        return 'Zagueiro central esquerdo';

      case 'cb':
        return 'Zagueiro';

      case 'rcb':
        return 'Zagueiro central direito';

      case 'rb':
        return 'Zagueiro Lateral direito';

      case 'sw':
        return 'Especial';

      case 'gk':
        return 'Goleiro';

      case 'none':
        return 'Nenhum';

      default:
        return '';
    }
  }

  checkIfOther(countryCode: any) {
    if (countryCode == 'other') {
      return '';
    } else {
      return countryCode;
    }
  }

  openTerms() {
    this.bottomSheet.open(TermsSheetComponent);
  }

  updateTerms(status: boolean) {
    this.isAccepted.set(status);
  }

  onSubmit() {
    if (this.registrationForm.invalid) {
      this.registrationForm.markAllAsTouched();
      return;
    }

    const userId = this.authService.userProfile()?.userId;
    if (!userId) {
      console.error('No userId found');
      return;
    }

    const formValues = this.registrationForm.getRawValue();
    const payload = {
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      placeOfResidence: formValues.city,
      firstPosition: this.mapPositionToAbbreviation(formValues.position),
      preferredContactMethod: {
        contactMethodType: 'whatsapp',
        value: this.checkIfOther(formValues.countryCode) + formValues.phoneNumber
      }
    };

    const requests: any = {
      profilePatch: this.profileHttp.patchProfile(userId, payload).pipe(
        catchError(err => of({ hasError: true, error: err }))
      )
    };

    if (!this.hasClub && this.registrationForm.getRawValue().club) {
      const date = new Date().toISOString();

      const clubPayload = {
        activeClub: true,
        clubName: formValues.club,
        startDate: date,
      }

      requests.addClub = this.profileHttp.addClubToUser(userId, clubPayload).pipe(
        catchError(err => of({ hasError: true, error: err }))
      );
    }

    forkJoin(requests).pipe(
      switchMap((results: any) => {
        const failedRequests = Object.values(results).filter((r: any) => r?.hasError);

        if (failedRequests.length > 0) {
          failedRequests.forEach((fail: any) => {
            this.checkErrorStatus(fail.error);
          });

          throw new Error('One or more requests failed')
        }
        return this.competitionHttp.registerUserForCompetition(userId, this.competitionId, {}).pipe(
          catchError(err => {
            this.checkErrorStatus(err);
            throw err;
          })
        );
      })
    ).subscribe({
      next: () => this.router.navigate(['/aguai-2026/registration-success']),
      error: () => { }
    })
  }

  checkErrorStatus(error: any) {
    if (error.status === 409) {
      alert('Você já está participando desta competição! Dá um pulo lá no CUJU e foca nos exercícios pra melhorar sua pontuação.');
    } else {
      alert('Vish, deu ruim aqui. Não conseguimos processar seus dados agora. Que tal dar uma revisada e tentar de novo?');
    }
  }
}

